const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/kisim.ts';
import {
    FormatMessageFn,
    KeyValuePair,
    KISIM_STD_DATE_TIME_FORMAT,
    KISIM_TIME_FORMAT,
    KISIM_TIME_OPEN,
} from '../defs/generalDefs';
import moment, { Moment } from 'moment';
import _ from 'lodash';
import { sortObjectsByPath } from './data';
import { v4 as uuidv4 } from 'uuid';
import {
    CaseType,
    EventData,
    KISIMObject,
    MetaKatItem,
    MetaKatItemReference,
    OrderData,
    StructuredAppointment,
} from '../defs/kisim';
import { AppointmentObject, ILocation, IPerson } from '../defs/notReallyKisim';

import { formatStandard } from './dateTime/dateTime';

export const KONTINUIERLICH_DAT = '(kont.)';
export const NOSCHEMA_DAT = '';

export const mapVerordnungen = (verordnungen: KeyValuePair[], keepFields: String[] = []) => {
    return verordnungen
        .map((verordnung: KeyValuePair) => {
            const orderId = uuidv4();
            const lastEventDate = verordnung.lastEvent?.STOPDAT ? moment(verordnung.lastEvent.STOPDAT) : undefined;
            const mappedVerordnung: KeyValuePair = {
                orderId,
                Applications: _.compact(
                    (verordnung.Applications || []).map((hotspot: KeyValuePair) => {
                        const hotspotMoment = moment(hotspot.DAT);
                        const diffToLastEvent = lastEventDate
                            ? hotspotMoment.diff(lastEventDate, 'minutes')
                            : undefined;
                        return {
                            DAT: hotspot.DAT,
                            STRDAT: hotspotMoment.format('HH:mm'),
                            KAT: verordnung.KAT,
                            FKEY: verordnung.FKEY,
                            NAME: verordnung.NAME,
                            ORDERTXT: verordnung.ORDERTXT,
                            parentId: orderId,
                            diffToLastEvent,
                        };
                    })
                ),
            };
            _.uniq(
                _.concat(keepFields, ['NAME', 'FKEY', 'KIND', 'ORDERTXT', 'STARTDAT', 'STOPDAT', 'lastEvent'])
            ).forEach((field: string) => (mappedVerordnung[field] = verordnung[field]));
            return mappedVerordnung;
        })
        .sort(sortObjectsByPath('FKEY'));
};

export const filterHotspots =
    (hotspotTolerance: number | undefined, maxMoment: Moment) => (verordnung: KeyValuePair) => {
        const verordnungCopy = _.cloneDeep(verordnung);
        // remove all applications beyond maxMoment
        verordnungCopy.Applications = _.filter(verordnungCopy.Applications, (application: KeyValuePair) => {
            return moment(application.DAT).isSameOrBefore(maxMoment);
        });
        // remove handled hotspots
        verordnungCopy.Applications = _.filter(
            verordnungCopy.Applications,
            (application: KeyValuePair, index: number) => {
                // no last event, keep all hotspots
                if (_.isUndefined(application.diffToLastEvent)) {
                    return true;
                }
                // hotspot tolerance is not set, keep always the last hotspot
                if (_.isUndefined(hotspotTolerance) && index === verordnungCopy.Applications.length - 1) {
                    return true;
                }
                // keep hotspot only if last event is after hotspot tolerance
                return application.diffToLastEvent > (hotspotTolerance ?? 0);
            }
        );
        return verordnungCopy;
    };

export const getVerordnungById = (verordnungen: KeyValuePair[], id: string) => {
    return _.find(verordnungen, (verordnung: KeyValuePair) => verordnung.orderId === id);
};

export const createDummyApplicationForSpecialSchemas = (verordnungen: KeyValuePair[]) => {
    _.forEach(verordnungen, (verordnung: KeyValuePair) => {
        const lastEventDate = verordnung.lastEvent?.STOPDAT ? moment(verordnung.lastEvent.STOPDAT) : undefined;
        if (verordnung.CONTENT?.Mode === 'Continuous') {
            verordnung.Applications = [
                {
                    DAT: '1900-01-01 00:00:00',
                    STRDAT: KONTINUIERLICH_DAT,
                    KAT: verordnung.KAT,
                    FKEY: verordnung.FKEY,
                    NAME: verordnung.NAME,
                    ORDERTXT: verordnung.ORDERTXT,
                    parentId: verordnung.orderId,
                },
            ];
        } else if (verordnung.CONTENT?.Mode === 'Once' || verordnung.STARTDAT === verordnung.STOPDAT) {
            const diffToLastEvent =
                lastEventDate && verordnung.STARTDAT
                    ? moment(verordnung.STARTDAT)?.diff?.(lastEventDate, 'minutes')
                    : undefined;
            verordnung.Applications = [
                {
                    DAT: verordnung.STARTDAT,
                    STRDAT: moment(verordnung.STARTDAT).format('HH:mm'),
                    KAT: verordnung.KAT,
                    FKEY: verordnung.FKEY,
                    NAME: verordnung.NAME,
                    ORDERTXT: verordnung.ORDERTXT,
                    parentId: verordnung.orderId,
                    diffToLastEvent,
                },
            ];
        } else if (!verordnung.CONTENT?.Mode) {
            verordnung.Applications = [
                {
                    DAT: '1900-01-01 00:00:00',
                    STRDAT: NOSCHEMA_DAT,
                    KAT: verordnung.KAT,
                    FKEY: verordnung.FKEY,
                    NAME: verordnung.NAME,
                    ORDERTXT: verordnung.ORDERTXT,
                    parentId: verordnung.orderId,
                },
            ];
        }
    });
};

export const getVerordnungenWithoutHotspots = (verordnungen: KeyValuePair[]): KeyValuePair => {
    return verordnungen.filter((verordnung: KeyValuePair) => {
        return verordnung.Applications.length === 0;
    });
};

export const getHotspotsGroupedByTime = (verordnungen: KeyValuePair[]): KeyValuePair => {
    return verordnungen.reduce((aggregator: KeyValuePair, verordnung: KeyValuePair) => {
        verordnung.Applications.forEach((hotspot: KeyValuePair) => {
            aggregator[hotspot.DAT] = aggregator[hotspot.DAT] ?? [];
            aggregator[hotspot.DAT].push(hotspot);
            aggregator[hotspot.DAT] = aggregator[hotspot.DAT].sort(sortObjectsByPath('FKEY'));
        });
        return aggregator;
    }, {});
};

export const getHotspotsWithMaxMoment = (hotspotsGroupedByTime: KeyValuePair, max: Moment): KeyValuePair => {
    const clone = _.cloneDeep(hotspotsGroupedByTime);
    const hotspotDates = Object.keys(clone);
    hotspotDates.forEach((hotspotDate: string) => {
        if (moment(hotspotDate).isAfter(max)) {
            delete clone[hotspotDate];
        }
    });
    return clone;
};

export const sortVerordnungenByDisplayedHotspot = (verordnungen: KeyValuePair[]): KeyValuePair[] => {
    return _.sortBy(verordnungen, [
        (verordnung: KeyValuePair) => verordnung.Applications?.[0]?.DAT || '3000-12-12 00:00:00',
        (verordnung: KeyValuePair) => verordnung.FKEY,
    ]);
};

export const insertExternalDocumentations = (
    filteredVerordnungen: KeyValuePair[],
    externalMonitorEvents: KeyValuePair[],
    externalMonitorVisum: string,
    whitelistedExtMonFkeys: string[]
) => {
    _.forEach(externalMonitorEvents, (event: KeyValuePair) => {
        if (event.VISUM !== externalMonitorVisum) {
            return;
        }
        if (whitelistedExtMonFkeys.indexOf(event.FKEY.toLowerCase()) < 0) {
            return;
        }
        let handled = false;
        _.forEach(_.sortBy(filteredVerordnungen, 'ORDERNR'), (order: KeyValuePair) => {
            if (handled) {
                return;
            }
            if (order.FKEY !== event.FKEY) {
                return;
            }
            if (order.STARTDAT > event.STARTDAT || event.STOPDAT < event.STARTDAT) {
                return;
            }
            handled = true;
            if (!order.Applications || order.Applications.length < 1) {
                return;
            }
            if (order.lastEvent && order.lastEvent.STARTDAT > event.STARTDAT) {
                return;
            }
            order.lastEvent = event;
            const lastEventDate = order.lastEvent?.STOPDAT ? moment(order.lastEvent.STOPDAT) : undefined;
            _.forEach(order.Applications, (hotspot: KeyValuePair) => {
                const hotspotMoment = moment(hotspot.DAT);
                const diffToLastEvent = lastEventDate ? hotspotMoment.diff(lastEventDate, 'minutes') : undefined;
                hotspot.diffToLastEvent = diffToLastEvent;
            });
        });
    });
};

export const getStartStopText = (kisimItem: KeyValuePair) => {
    return _.compact([
        formatStandard(kisimItem.STARTDAT),
        kisimItem.STARTDAT === kisimItem.STOPDAT ? null : formatStandard(kisimItem.STOPDAT),
    ]).join(' - ');
};

export const matchesTimePeriod = (
    kisimItem: KeyValuePair,
    minMoment?: Moment,
    maxMoment?: Moment,
    dateField: string = 'DAT',
    parseFormat: string = KISIM_TIME_FORMAT
) => {
    const dateMoment = moment(kisimItem[dateField], parseFormat);
    return (
        (minMoment === undefined || minMoment.isSameOrBefore(dateMoment)) &&
        (maxMoment === undefined || maxMoment.isSameOrAfter(dateMoment))
    );
};

export const filterByTimePeriod = (
    minMoment?: Moment,
    maxMoment?: Moment,
    dateField?: string,
    parseFormat?: string
) => {
    return (kisimItem: KeyValuePair) => matchesTimePeriod(kisimItem, minMoment, maxMoment, dateField, parseFormat);
};

export const getLatestUniqueMassnahmen = (
    massnahmen: KeyValuePair[],
    getUniquenessIdentifier: (massnahme: KeyValuePair) => string
): KeyValuePair[] => {
    const latestUniqueMassnahmen: KeyValuePair = {};
    massnahmen.forEach(massnahme => {
        const uniquenessIdentifier = getUniquenessIdentifier(massnahme);
        const currentLatestMassnahme = latestUniqueMassnahmen[uniquenessIdentifier];
        if (currentLatestMassnahme) {
            if (massnahme.STARTDAT > currentLatestMassnahme.STARTDAT) {
                latestUniqueMassnahmen[uniquenessIdentifier] = massnahme;
            }
        } else {
            latestUniqueMassnahmen[uniquenessIdentifier] = massnahme;
        }
    });
    return Object.keys(latestUniqueMassnahmen).map(key => latestUniqueMassnahmen[key]);
};

export const getLatestMassnahme = (massnahmen: KeyValuePair[]) => {
    return getLatestUniqueMassnahmen(massnahmen, _ => 'fixed')[0];
};

export const convertListObjectToArray = (listObject: { [key: string]: KISIMObject } = {}): KISIMObject[] => {
    return listObject ? Object.keys(listObject).map(key => listObject[key]) : [];
};

/**
 * Location can be either be set from
 * 1) If RESNAME + KAT == KONS/USZIMMER
 * 2) If ORT available (= Gruppentermin)
 * 3) If TEILNEHMER is of KAT == "KONS"
 * @param appointment
 * @return locations
 */
const getLocations = (appointment: StructuredAppointment) => {
    const locations: ILocation[] = [];
    if (appointment.RESNAME) {
        if (appointment.KAT === 'KONS' || appointment.KAT === 'UZIMMER') {
            locations.push({
                name: appointment.RESNAME,
            });
        }
    }
    Object.values(appointment.ORT || {}).forEach((location: KeyValuePair) =>
        locations.push({
            name: location.NAME,
        })
    );

    Object.values(appointment.TEILNEHMER || {})
        .filter((teilnehmer: KeyValuePair) => teilnehmer.KAT === 'KONS')
        .forEach((teilnehmer: KeyValuePair) =>
            locations.push({
                name: teilnehmer.DISPLAY,
            })
        );
    return locations;
};

/**
 * Practitioner can be either be set from
 * 1) If RESNAME + KAT == USER
 * 2) If BEHANDLER available (= Gruppentermin)
 * 3) If TEILNEHMER is of KAT == "USER"
 * @param appointment
 * @return practitioners
 */
export const getPractitioners = (appointment: StructuredAppointment) => {
    const practitioners: IPerson[] = [];
    if (appointment.RESNAME) {
        if (appointment.KAT === 'USER') {
            practitioners.push({
                type: appointment.KAT as string,
                name: appointment.RESNAME,
            });
        }
    }
    Object.values(appointment.BEHANDLER || {}).forEach((behandler: KeyValuePair) =>
        practitioners.push({
            type: behandler.KAT,
            name: behandler.DISPLAY,
        })
    );
    Object.values(appointment.TEILNEHMER || {})
        .filter((teilnehmer: KeyValuePair) => teilnehmer.KAT === 'USER')
        .forEach((teilnehmer: KeyValuePair) =>
            practitioners.push({
                type: teilnehmer.KAT,
                name: teilnehmer.DISPLAY,
            })
        );
    return practitioners;
};

/**
 * Checks if the url has https or http. If this is missing we add // such that the string will be interpreted as a valid url
 * @param url
 */
export const getValidUrl = (url: string) => {
    return url.startsWith('https://') || url.startsWith('http://') ? url : '//'.concat(url);
};

export const convertStructuredAppointmentToAppointmentObject = (
    appointment: StructuredAppointment,
    formatMessage: FormatMessageFn
): AppointmentObject => {
    let startDatForPatient;
    const EINTRITTDAT = appointment.EINTRITTDAT;
    const EINTRITTTIME = appointment.EINTRITTTIME;
    if (EINTRITTDAT && EINTRITTTIME) {
        startDatForPatient = moment(`${EINTRITTDAT} ${EINTRITTTIME}`, KISIM_STD_DATE_TIME_FORMAT).format(
            KISIM_TIME_FORMAT
        );
    }

    return {
        resname: appointment.titleFunction ? appointment.titleFunction(formatMessage) : appointment.RESNAME,
        termnr: appointment.TERMNR,
        startDat: appointment.STARTDAT || '',
        stopDat: appointment.STOPDAT || '',
        startDatForPatient: startDatForPatient || '',
        patnr: appointment.PATNR || '',
        casenr: appointment.FALLNR || '',
        title: appointment.KIND?.TerminartForMakro || appointment.KIND?.DISPLAY || '',
        termLocation: appointment.TITLE || '',
        ressourcenStandort: {
            Standort: appointment.RESSOURCENSTANDORT?.Standort,
            Adresse: appointment.RESSOURCENSTANDORT?.Adresse,
            TelefonStandort: appointment.RESSOURCENSTANDORT?.TelefonStandort,
            KAT: appointment.RESSOURCENSTANDORT?.KAT,
            FKEY: appointment.RESSOURCENSTANDORT?.FKEY,
        },
        responsiblePerson: appointment.TEILNEHMER?.['1']?.DISPLAY,
        allDayEvent: appointment.ALLDAYEVENT || '',
        kind: appointment.KIND?.FKEY || '',
        interventions: Object.values(appointment.EINGRIFF || {}).map((intervention: KeyValuePair) => ({
            name: intervention.NAME,
        })),
        opTeam: Object.values(appointment.OPTEAM || {}).map((teamMember: KeyValuePair) => ({
            name: teamMember.DISPLAY,
        })),
        practitioners: getPractitioners(appointment),
        locations: getLocations(appointment),
        externalLink: (appointment.EXTERNALLINK && getValidUrl(appointment.EXTERNALLINK)) || '',
        typ: appointment.TYP,
        termKat: appointment.TERMKAT || '',
        patientByEmail: appointment.CONTENT?.Header?.Patient?.EMAIL || '',
        // The following properties are just there because they are somehow expected
        allergies: false,
        caseType: CaseType.none,
        dateOfBirth: '',
        devices: [],
        gender: '',
        groupid: '',
        isolation: false,
        kindColor: { r: 255, g: 255, b: 255 },
        participants: [],
        patients: [],
        remarks: '',
        status: '',
        statusColor: { r: 255, g: 255, b: 255 },
    };
};

export const appointmentIsWithin24Hours = (appointment: StructuredAppointment) => {
    return moment(appointment.STARTDAT).diff(moment(), 'hours') <= 24;
};

export const getOrderName = (order: OrderData) =>
    [_.get(order, 'CONTENT.MediField.Product'), _.get(order, 'CONTENT.MediField.Article')].join(' ');

export const getOrderDescription = (order: OrderData) => _.get(order, 'CONTENT.MediField.Ingred');

export const metaKatItemToMetaKatItemRef = (item: MetaKatItem): MetaKatItemReference => {
    return _.pick(item, ['KAT', 'FKEY', 'NAME']);
};

export const getFirstItemFromKisimList = (list: any[] | KeyValuePair | undefined) => {
    return Array.isArray(list) ? list[0] : list?.['1'];
};

export const getPrescribedDuration = (order: OrderData) => {
    const schedule = order.CONTENT?.Schedule;
    const durationData = getFirstItemFromKisimList(schedule)?.Duration;
    const unit = durationData?.Unit;
    if (unit) {
        // 3 == minutes
        // not clear if another unit is ever used...
        if (unit === '3') {
            const value = durationData.Val;
            if (value === '0') {
                return Infinity;
            } else if (value) {
                return parseInt(value, 10);
            }
        }
    }

    return undefined;
};

export const getStopDatBasedOnOrderedDuration = (startDat: string, order: OrderData) => {
    // Appears to be simplified. Not certain if it covers all cases. In case a schema is used, all entries appear to get a copy of the duration
    // since it looks like duration can only be specified once
    const duration = getPrescribedDuration(order);

    if (duration === Infinity) {
        return KISIM_TIME_OPEN;
    } else if (Number.isFinite(duration)) {
        return moment(startDat, KISIM_TIME_FORMAT).add(duration, 'minutes').format(KISIM_TIME_FORMAT);
    }

    return startDat;
};

export const getStopDatBasedOnDurationInEvent = (startDat: string, event: EventData) => {
    const unit = event.CONTENT?.DURATIONUNIT;
    if (unit) {
        // not clear if another unit is ever used...
        if (unit === 'min') {
            const value = event.CONTENT?.DURATIONVAL;
            if (value) {
                const minutes = parseInt(value);
                return moment(startDat, KISIM_TIME_FORMAT).add(minutes, 'minutes').format(KISIM_TIME_FORMAT);
            }
        }
        return event.STOPDAT;
    } else if (event.CONTENT?.DURATION) {
        const value = event.CONTENT.DURATION;
        const minutes = parseInt(value);
        return moment(startDat, KISIM_TIME_FORMAT).add(minutes, 'minutes').format(KISIM_TIME_FORMAT);
    }

    return startDat;
};
